/**
 * PORTED — RO-3-00 / #44398.
 * Origin: `excellent/app/_modes/mode-play.tsx`.
 *
 * Changes:
 *   `"use client"`                      -> dropped (inert in this host)
 *   `useRouter` from next/navigation    -> `useNavigate` from @excellent/ui/router
 *   `../_chrome/play-signal`            -> `../chrome/play-signal` (the ported copy)
 *
 * ── Its two `@/modes/*` deps are NOT duplicated, deliberately ──
 *
 * `@/modes/play-route` (188 LOC) and `@/modes/modes-registry` (230 LOC) stay where
 * they are and are imported through the `@/` alias, which `client/vite.config.ts`
 * already maps to `excellent/src/*`. Both are pure string/route resolvers with no
 * server sink anywhere in their closure (`play-route -> domains -> modes-registry`,
 * checked), which is exactly the condition that alias documents as its contract:
 * "only PURE modules may be imported through it from client code (PRD §5 I4)".
 *
 * Copying them would have added 418 LOC of duplication to save nothing, and would
 * have forked the kind/route mapping that this component's scope predicate and
 * `resolvePlayRoute`'s Dive-in target BOTH read — the comment in the body below is
 * explicit that those two must never disagree. A fork is precisely how they would.
 *
 * `play.queue` is mounted on the Hono host, so this is live here.
 */

import { useNavigate } from "@excellent/ui/router";
import { Card, CardContent, CardHeader, CardTitle, PlayStrip } from "@excellent/ui";
import { useLiveResource } from "@excellent/ui/api/live";
import {
  play,
  type PlayActionDTO,
  type PlaySignal,
} from "@excellent/ui/api/contracts/play";
import { resolvePlayRoute, playHomeMode, playObjectKind } from "@/modes/play-route";
import type { ModeSlug } from "@/modes/modes-registry";
import { postPlaySignal } from "../chrome/play-signal";

/**
 * ModePlay — the real Play queue, scoped-projection style (UI-rewrite P5;
 * Interaction Specs §A1). Play is ONE engine rendered in three places: globally
 * (the driver), per-mode (the Queue view), and per-object (the Recommendations
 * section). This is the per-mode / per-object surface: it reads the same live
 * `/api/play/queue`, renders the ranked PlayStrip, and posts every Do / Defer /
 * Dismiss / Delegate back as a feedback signal (the recursive self-improvement
 * ledger).
 *
 * `filterUid` narrows the queue to actions that reference one object (the
 * Recommendations section); omitted, it shows the whole ranked queue (Queue view).
 */
export function ModePlay({
  title,
  limit = 6,
  filterUid,
  mode,
  kinds,
  wrapInCard = true,
  hideWhenEmpty = false,
  emptyMessage,
}: {
  title?: string;
  limit?: number;
  filterUid?: string;
  /** Mode-home / Queue scope (§2.4): keep only actions whose HOME mode is this
   *  one (`playHomeMode`). A pure client predicate over the one global ranked
   *  queue — never a second fetch or re-rank (Invariant P-1, aggregate-don't-fork). */
  mode?: ModeSlug;
  /** Domain-view scope (§2.4): keep only actions whose object kind ∈ kinds.
   *  Takes precedence over `mode` (a domain is narrower than its mode). */
  kinds?: string[];
  wrapInCard?: boolean;
  /** Secondary-slot strips (mode/domain dashboards, object Recommendations) pass
   *  this so an empty scoped queue renders NOTHING instead of a filler card. The
   *  dedicated Queue view omits it, keeping an honest empty state. */
  hideWhenEmpty?: boolean;
  emptyMessage?: string;
}) {
  const navigate = useNavigate();
  const { data, mutate } = useLiveResource(play.queue, {}, {
    topic: "play",
    refreshInterval: 60_000,
  });

  const all = data?.actions ?? [];
  const byUid = filterUid
    ? all.filter(
        (a) => a.key.includes(filterUid) || (a.ctaHref ?? "").includes(filterUid),
      )
    : all;
  // §2.4 surface scope: a domain view scopes to its object `kinds`; a mode
  // home/queue scopes to its `mode` (the action's HOME mode via playHomeMode).
  // Both derive from the SAME kind/route mapping resolvePlayRoute uses, so scope
  // and Dive-in never disagree.
  //
  // Crucially this does NOT fall back to the global queue when nothing matches.
  // The old `scoped.length ? scoped : byUid` is exactly why a 1-customer workspace
  // showed "Connect your first account"/"Run your function audits" here: no
  // customer action existed, so the strip silently reverted to the global setup
  // nudges. A scoped strip is honestly empty — and as a secondary slot
  // (`hideWhenEmpty`) it renders NOTHING rather than a filler card; always-have-
  // work lives on the global driver/Queue (no mode + no kinds → shows everything).
  const actions =
    kinds && kinds.length > 0
      ? byUid.filter((a) => {
          const k = playObjectKind(a);
          return k != null && kinds.includes(k);
        })
      : mode
        ? byUid.filter((a) => playHomeMode(a) === mode)
        : byUid;
  const scopedToSurface = Boolean(filterUid || (kinds && kinds.length > 0) || mode);
  const total = scopedToSurface ? actions.length : (data?.total ?? actions.length);

  // Secondary slot + empty scoped queue → render NOTHING (no card, no filler).
  if (hideWhenEmpty && actions.length === 0) return null;

  const signal = (action: PlayActionDTO, sig: PlaySignal, reason?: string) => {
    // The canonical feedback post + live re-rank fan-out (shared with the Play
    // dialog + home list). Re-fetch locally when it reports a decision signal.
    void postPlaySignal(action, sig, reason).then((changed) => {
      if (changed) void mutate();
    });
  };

  const onDo = (action: PlayActionDTO) => {
    signal(action, "started");
    // Route into the REAL Domain view (§2.5) — never a generic table.
    navigate.push(resolvePlayRoute(action));
  };

  const strip = (
    <PlayStrip
      actions={actions}
      total={total}
      limit={limit}
      onDo={onDo}
      onDefer={(a) => signal(a, "skipped")}
      onDismiss={(a, reason) => signal(a, "dismissed", reason)}
      onDelegate={(a) => {
        // Delegate-to-agent (§2.3.1). Leftover `/agents/goals/new` remounts
        // ProductChrome; hop to `/` (VerificationShell). Do not restore /agents.
        signal(a, "started");
        navigate.push("/");
      }}
      emptyMessage={
        emptyMessage ??
        (filterUid
          ? "No recommendations for this object right now."
          : "Nothing needs you right now — you're clear.")
      }
    />
  );

  if (!wrapInCard) return strip;

  // Titled (the dedicated Queue view) → a full Card with a header. Untitled (the
  // per-mode / per-object slot) → a compact frame: minimal padding so the 1–3
  // rows read as a quiet attention strip, not a card moat.
  if (title) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
        </CardHeader>
        <CardContent>{strip}</CardContent>
      </Card>
    );
  }
  return (
    <Card className="py-0">
      <CardContent className="py-2">{strip}</CardContent>
    </Card>
  );
}
